import { evaluate } from 'mathjs';
import { UnitModule } from '../unit.module';
import { UnitService } from '../services/unit.service';

export interface UnitInterface {
  id: string;
  name: string;
  displayName: string;
  translationId: string;
  unitType: string;
  factor: string;
  order: number;
  useMeasurementSystem: boolean;
  measurementSystem?: string;
  equivalentUnit?: string;
}

export class Unit {
  id: string;
  name: string;
  displayName: string;
  translationId: string;
  unitType: string;
  factor: string;
  order: number;
  useMeasurementSystem: boolean;
  measurementSystem: string;
  equivalentUnit: string;

  unitService: UnitService;

  constructor(param: UnitInterface = {
    id: null,
    name: null,
    displayName: null,
    translationId: null,
    unitType: null,
    factor: null,
    order: 0,
    useMeasurementSystem: false,
    measurementSystem: null,
    equivalentUnit: null,
  }) {
    if (param) {
      this.id = param.id;
      this.name = param.name;
      this.displayName = Unit.parseUnitDisplayName(param.displayName);
      this.translationId = param.translationId;
      this.unitType = param.unitType;
      this.factor = param.factor;
      this.order = param.order;
      this.useMeasurementSystem = param.useMeasurementSystem;
      this.measurementSystem = param.measurementSystem;
      this.equivalentUnit = param.equivalentUnit;
    }
    this.injectUnitService();
  }

  /**
   * Converts the given value between the given units.
   *
   * @param value given value to convert between units
   * @param sourceUnitId id of the unit the the given value is currently in
   * @param targetUnitId id of the unit to convert the given value to
   */
  static convertValue(value: number, sourceUnitId: string, targetUnitId: string) {
    if (value == null) {
      return value;
    }

    const unitService = UnitModule.injector.get(UnitService);

    const sourceUnit = unitService.getUnitById(sourceUnitId);
    if (!sourceUnit) {
      throw new Error(`Invalid origin unit: ${sourceUnitId}`);
    }

    const targetUnit = unitService.getUnitById(targetUnitId);
    if (!targetUnit) {
      throw new Error(`Invalid target unit: ${targetUnitId}`);
    }

    if (sourceUnit.unitType !== targetUnit.unitType) {
      throw new Error(`Cannot convert between units of different types. ${sourceUnit.unitType} vs. ${targetUnit.unitType}`);
    }

    const baseValue = sourceUnit.convertToBase(value);
    return targetUnit.convertFromBase(baseValue);
  }

  /**
   * Parses display name into subscript/superscript
   */
  static parseUnitDisplayName(name: string) {
    if (name === null || name === undefined) return name;
    name = name.replace(/_{[^}]*}/g, (n: string) => {
      return n.replace('_{', '<sub>').replace('}', '</sub>');
    });
    name = name.replace(/\^{[^}]*}/g, (n: string) => {
      return n.replace('^{', '<sup>').replace('}', '</sup>');
    });
    name = name.replace(/_\d/g, (n: string) => {
      return n.replace('_', '<sub>') + '</sub>';
    });
    name = name.replace(/\^\d/g, (n: string) => {
      return n.replace('^', '<sup>') + '</sup>';
    });
    return name;
  }

  static checkForType(sourceUnitId: string, targetUnitId: string){
    const unitService = UnitModule.injector.get(UnitService);

    const sourceUnit = unitService.getUnitById(sourceUnitId);
    if (!sourceUnit) {
       return false;
    }

    const targetUnit = unitService.getUnitById(targetUnitId);
    if (!targetUnit) {
      return false;
    }

    if (sourceUnit.unitType !== targetUnit.unitType) {
      return false;
    }

    return true;
  }

  clearUnitService() {
    this.unitService = null;
  }

  injectUnitService() {
    this.unitService = UnitModule.injector.get(UnitService);
  }

  /**
   * Create a deep copy of this unit without the unit service reference
   */
  copyWithoutUnitService(): Unit {
    const unitCopy = new Unit(this);
    unitCopy.clearUnitService();
    return unitCopy;
  }

  /**
   * Convert the given value to the base unit from this unit
   */
  convertToBase(value: number) {
    if (value == null || isNaN(value)) {
      throw new Error('Invalid value. Value must be a number ' + value);
    }

    if (this.factor != null) {
      return evaluate(this.factor.replace(/V/g, String(value)));
    } else {
      throw new Error('Invalid unit factor ' + this.factor);
    }
  }

  /**
   * Convert the given value from the base unit to this unit
   */
  convertFromBase(value: number) {
    if (value == null || isNaN(value)) {
      throw new Error('Invalid value. Value must be a number ' + value);
    }

    if (this.factor) {
      let factor = this.factor;
      var map = { '*': '/', '/': '*' };
      factor = factor.replace(/[*\/]/g, function (k) {
        return map[k];
      });

      return evaluate(factor.replace(/V/g, String(value)));
    } else {
      throw new Error('Invalid unit factor ' + this.factor);
    }
  }

  /**
   * Converts the given value from this unit to the specified unit
   * If the current value is not the base value, it will be converted to the
   * base first and then to the desired unit.
   */
  convertToUnit(value: number, targetUnitOrUnitId: Unit | string): number {
    const baseValue = this.convertToBase(value);
    let targetUnit: Unit;
    if (targetUnitOrUnitId instanceof String) {
      targetUnit = this.unitService.getUnitById(String(targetUnitOrUnitId));
      if (!targetUnit) {
        throw new Error(`Invalid target unit: ${String(targetUnitOrUnitId)}`);
      }
    } else {
      targetUnit = targetUnitOrUnitId as Unit;
    }

    if (this.unitType !== targetUnit.unitType) {
      throw new Error(`Cannot convert between units of different types. ${this.unitType} vs. ${targetUnit.unitType}`);
    }
    return targetUnit.convertFromBase(baseValue);
  }

  /**
   * Returns the unit matching the equivalent unit id. Returns null if a
   * matching unit is not found.
   */
  getEquivalentUnit(): Unit {
    if (this.useMeasurementSystem && this.equivalentUnit !== null && this.equivalentUnit !== undefined) {
      return this.unitService.getUnitById(this.equivalentUnit);
    }
    return null;
  }
}
